import { cn } from "@/common/helpers/utils";
import { ButtonColorVariant, ButtonSize, ButtonVariant, buttonVariants } from "@/components/ui/button";
import {
  FileUpload,
  FileUploadClear,
  FileUploadDropzone,
  FileUploadItem,
  FileUploadItemDelete,
  FileUploadItemPreview,
  FileUploadItemProgress,
  FileUploadTrigger,
} from "@/components/ui/file-upload";
import { XIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const S3FileSizeLimits = {
  GlobalMax: 60 * 1024 * 1024,
  Image: 60 * 1024 * 1024,
} as const;

export const S3FileTypes = {
  Image: [
    { mimeType: "image/jpeg", extensions: ".jpeg" },
    { mimeType: "image/jpg", extensions: ".jpg" },
    { mimeType: "image/png", extensions: ".png" },
    { mimeType: "image/svg+xml", extensions: ".svg" },
    { mimeType: "image/webp", extensions: ".webp" },
  ],
};

interface FileUploaderProps {
  maxFileSize?: number;
  maxFiles?: number;
  value?: File | File[] | null;
  onFileSelect?: (file: File | null) => void;
  onUpload?: (file: File) => Promise<void>;
  onFileRemove?: (file: File) => void;
  label?: string;
}

export const FileUploader = ({
  maxFileSize = S3FileSizeLimits.Image,
  maxFiles,
  value: externalValue,
  onFileSelect,
  onUpload,
  onFileRemove,
  label,
}: FileUploaderProps) => {
  const { t } = useTranslation("common");
  const [error, setError] = useState<string>("");

  const maxSize = maxFileSize;
  const acceptedTypes = S3FileTypes.Image;
  const acceptString = acceptedTypes.map((t) => t.mimeType).join(",");
  const multiple = maxFiles !== 1;

  const files = externalValue ? (Array.isArray(externalValue) ? externalValue : [externalValue]) : [];

  const validateFile = (file: File): boolean => {
    if (file.size > maxSize) {
      const sizeMB = (maxSize / (1024 * 1024)).toFixed(0);
      setError(`File size exceeds ${sizeMB}MB limit`);
      return false;
    }

    const isValidType = acceptedTypes.some((t) => t.mimeType === file.type);
    if (!isValidType) {
      const extensions = acceptedTypes.map((t) => t.extensions).join(", ");
      setError(`Invalid file type. Accepted: ${extensions}`);
      return false;
    }

    setError("");
    return true;
  };

  const handleValueChange = (newFiles: File[]) => {
    if (onFileRemove && files.length > newFiles.length) {
      const removedFiles = files.filter((oldFile) => !newFiles.some((newFile) => newFile.name === oldFile.name));
      removedFiles.forEach((file) => onFileRemove(file));
    }

    if (newFiles.length === 0) {
      onFileSelect?.(null);
      return;
    }

    if (multiple) {
      newFiles.forEach((file) => onFileSelect?.(file));
    } else {
      onFileSelect?.(newFiles[0] || null);
    }
  };

  const handleFileAccept = (file: File) => {
    if (!validateFile(file)) {
      return;
    }
  };

  const handleFileReject = (file: File, message: string) => {
    setError(`${file.name}: ${message}`);
  };

  const handleUploadFiles = async (
    files: File[],
    options: {
      onProgress: (file: File, progress: number) => void;
      onSuccess: (file: File) => void;
      onError: (file: File, error: Error) => void;
    },
  ) => {
    if (onUpload) {
      for (const file of files) {
        try {
          await onUpload(file);
          options.onSuccess(file);
        } catch (error) {
          options.onError(file, error as Error);
        }
      }
    }
  };

  return (
    <div className="space-y-2">
      <FileUpload
        value={files}
        maxFiles={maxFiles}
        maxSize={maxSize}
        accept={acceptString}
        onValueChange={handleValueChange}
        onFileAccept={handleFileAccept}
        onFileReject={handleFileReject}
        onUpload={onUpload ? handleUploadFiles : undefined}
        multiple={multiple}
      >
        <div className="flex flex-row items-end justify-between">
          <p className="text-sm font-medium">{label}</p>
          <FileUploadClear
            forceMount
            disabled={files.length === 0}
            className={cn(
              buttonVariants({
                variant: ButtonVariant.Outlined,
                colorVariant: ButtonColorVariant.Secondary,
                size: ButtonSize.Sm,
              }),
              "w-auto",
            )}
          >
            {t("ClearAll")}
          </FileUploadClear>
        </div>
        <FileUploadDropzone className="bg-input-background relative flex min-h-32 cursor-pointer rounded-md border-dashed">
          {files.length === 0 ? (
            <div className="flex h-full w-full items-center justify-center">
              <p className="text-muted-foreground text-center text-sm">
                {t(multiple ? "DropFilesOrClick" : "DropFileOrClick")}
              </p>
            </div>
          ) : (
            <div className="flex w-full flex-wrap gap-2 p-0">
              {files.map((file) => (
                <FileUploadItem
                  key={file.name}
                  value={file}
                  className="relative h-32 w-32 overflow-hidden border-0 p-0"
                >
                  <FileUploadItemPreview className="h-full w-full rounded-md [&>img]:transition-opacity [&>img]:data-uploading:opacity-30" />
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <FileUploadItemProgress variant="circular" size={48} />
                  </div>
                  <FileUploadItemDelete
                    className={cn(
                      "bg-background/80 hover:bg-background text-muted-foreground hover:text-error absolute top-1",
                      "right-1 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full transition-colors",
                    )}
                  >
                    <XIcon className="h-4 w-4" />
                  </FileUploadItemDelete>
                </FileUploadItem>
              ))}
            </div>
          )}
        </FileUploadDropzone>
        <FileUploadTrigger />
      </FileUpload>
      {error && <div className="text-error text-sm">{error}</div>}
    </div>
  );
};
